import React, { useState } from 'react';
import { Btn, Card } from './ui';
import { C, BADGES } from '../data/constants';

const RANKS = [
  { min: 0.9, ic: '🏆', tt: 'Pharaoh of Knowledge!',  sb: 'You have honoured the ancestors 👑' },
  { min: 0.7, ic: '🎉', tt: 'Scholar of Timbuktu!',   sb: 'Your wisdom is growing strong 📜' },
  { min: 0.5, ic: '💪', tt: 'Warrior of Truth!',       sb: 'Keep going — every answer matters ⚔️' },
  { min: 0.0, ic: '🌱', tt: 'Keep Learning!',           sb: 'Every expert started as a beginner 😊' },
];

export default function Results({ results, onPlayAgain, onHome, player }) {
  const [expanded, setExpanded] = useState(null);
  const [copied,   setCopied]   = useState(false);

  if (!results) return null;
  const { score, bestStreak, answers = [], timings = [], newBadges = [] } = results;
  const correct = answers.filter(a => a.correct).length;
  const total   = answers.length || 10;
  const avgT    = timings.length ? Math.round(timings.reduce((a, b) => a + b, 0) / timings.length) : 0;
  const pct     = total > 0 ? correct / total : 0;
  const rank    = RANKS.find(r => pct >= r.min) || RANKS[3];

  const handleShare = async () => {
    const name = player?.username ? `${player.username} scored` : 'I scored';
    const url  = window.location.origin;
    const text = `🌍 KABAAK — Know Your Roots\n\n${rank.ic} ${rank.tt}\n${name} ${score} pts · ✅ ${correct}/${total} correct · 🔥 ${bestStreak} streak\n\nThink you know African history better? Play KABAAK!\n${url}`;

    if (navigator.share) {
      try { await navigator.share({ title: 'KABAAK — Know Your Roots', text }); } catch {}
    } else {
      try {
        await navigator.clipboard.writeText(text);
        setCopied(true);
        setTimeout(() => setCopied(false), 2500);
      } catch {}
    }
  };

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <div className="scroll-area" style={{ padding: '20px 16px' }}>
        {/* Rank */}
        <div style={{ textAlign: 'center', padding: '1rem 0 .75rem' }}>
          <div className="pop" style={{ fontSize: 60, marginBottom: 8 }}>{rank.ic}</div>
          <div style={{ fontSize: 22, fontWeight: 900, marginBottom: 4 }}>{rank.tt}</div>
          <div style={{ fontSize: 13, color: C.muted }}>{rank.sb}</div>
        </div>

        {/* Score */}
        <div style={{ background: '#FFF9E8', border: `2px solid ${C.goldLight}`, borderRadius: 16, padding: '1rem', textAlign: 'center', marginBottom: 14, boxShadow: '0 3px 0 #D4A000' }}>
          <div style={{ fontSize: 50, fontWeight: 900, color: C.amber, lineHeight: 1 }}>{score}</div>
          <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>points ⭐</div>
        </div>

        {/* Stats */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 8, marginBottom: 14 }}>
          {[
            [`${correct}/${total}`, 'Correct ✅'],
            [bestStreak, 'Streak 🔥'],
            [`${avgT}s`, 'Avg ⚡'],
          ].map(([n, l]) => (
            <Card key={l} style={{ textAlign: 'center', padding: '10px 6px' }}>
              <div style={{ fontSize: 18, fontWeight: 900 }}>{n}</div>
              <div style={{ fontSize: 9, fontWeight: 700, color: C.muted, textTransform: 'uppercase', marginTop: 2 }}>{l}</div>
            </Card>
          ))}
        </div>

        {/* Share */}
        <button
          onClick={handleShare}
          style={{ width: '100%', padding: '14px 0', borderRadius: 14, fontSize: 15, fontWeight: 700, cursor: 'pointer', fontFamily: 'Roboto, sans-serif', background: copied ? C.green : C.blue, color: '#fff', border: 'none', boxShadow: `0 4px 0 ${copied ? '#1A7A40' : '#1A5A9A'}`, marginBottom: 10, transition: 'background .2s' }}>
          {copied ? '✅ Copied to clipboard!' : '📤 Share My Results'}
        </button>

        {/* Newly earned badges */}
        {newBadges.map(idx => {
          const badge = BADGES[idx];
          if (!badge) return null;
          return (
            <div key={idx} style={{ background: '#FFF9E8', border: `2px solid ${C.goldLight}`, borderRadius: 16, padding: 14, marginBottom: 10, display: 'flex', gap: 12, alignItems: 'center', boxShadow: '0 3px 0 #D4A000' }}>
              <div style={{ fontSize: 32 }}>{badge.ic}</div>
              <div>
                <div style={{ fontSize: 10, fontWeight: 700, color: C.amber, textTransform: 'uppercase', letterSpacing: '.08em', marginBottom: 3 }}>Title Earned! 🎉</div>
                <div style={{ fontSize: 15, fontWeight: 900 }}>{badge.name}</div>
                <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>{badge.how}</div>
              </div>
            </div>
          );
        })}

        {/* Question review */}
        {answers.length > 0 && (
          <>
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '.1em', textTransform: 'uppercase', color: C.muted, marginBottom: 8 }}>
              Review — tap any row to expand
            </div>

            {answers.map((a, i) => {
              const isOpen = expanded === i;
              return (
                <div key={i} style={{ marginBottom: 6, borderRadius: 12, border: `2px solid ${a.correct ? '#90DEB8' : C.border}`, overflow: 'hidden', background: '#fff' }}>
                  <div
                    onClick={() => setExpanded(prev => prev === i ? null : i)}
                    style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 12px', cursor: 'pointer' }}>
                    <span style={{ color: C.text, maxWidth: '72%', fontWeight: 500, fontSize: 12, lineHeight: 1.4 }}>
                      {i + 1}. {a.q}
                    </span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span style={{ padding: '3px 8px', borderRadius: 20, fontSize: 10, fontWeight: 700, whiteSpace: 'nowrap', background: a.correct ? '#E8F8F0' : '#FFF0EC', color: a.correct ? C.green : C.coral, border: `2px solid ${a.correct ? C.teal : C.coral}` }}>
                        {a.correct ? '✓' : '✗'}
                      </span>
                      <span style={{ fontSize: 12, color: C.muted }}>{isOpen ? '▲' : '▼'}</span>
                    </div>
                  </div>

                  {isOpen && (
                    <div style={{ borderTop: `2px solid ${C.border}`, padding: '10px 12px', background: '#FAFAF8' }}>
                      {a.options ? a.options.map((opt, oi) => {
                        const isCorrectOpt  = oi === a.correctIndex;
                        const isPickedWrong = oi === a.pickedIndex && !a.correct;
                        return (
                          <div key={oi} style={{ padding: '7px 10px', marginBottom: 5, borderRadius: 9, fontSize: 12, fontWeight: isCorrectOpt ? 700 : 500, background: isCorrectOpt ? '#E8F8F0' : isPickedWrong ? '#FFF0EC' : '#fff', border: `2px solid ${isCorrectOpt ? C.green : isPickedWrong ? C.coral : C.border}`, color: isCorrectOpt ? C.green : isPickedWrong ? C.coral : C.muted, display: 'flex', alignItems: 'center', gap: 8 }}>
                            {isCorrectOpt && <span>✓</span>}
                            {isPickedWrong && <span>✗</span>}
                            {opt}
                          </div>
                        );
                      }) : (
                        <div style={{ fontSize: 12, color: C.green, fontWeight: 700 }}>✓ {a.correctAnswer}</div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </>
        )}

        <div style={{ height: 14 }} />
        <Btn variant="green" onClick={onPlayAgain}>🎮 Play Again!</Btn>
        <Btn variant="white" onClick={onHome}>Back to Home</Btn>
        <div style={{ height: 16 }} />
      </div>
    </div>
  );
}
